export type PmFilterOperator =
  | 'equals'
  | 'contains'
  | 'starts_with'
  | 'ends_with'
  | 'greater_than'
  | 'less_than'
  | 'between'
  | 'in'
  | 'not_in';

const ALLOWED_OPERATORS: ReadonlySet<string> = new Set([
  'equals',
  'contains',
  'starts_with',
  'ends_with',
  'greater_than',
  'less_than',
  'between',
  'in',
  'not_in',
]);

export interface PmFilterRule {
  field: string;
  operator: PmFilterOperator;
  value: any;
}

export type PmFilterCombinator = 'and' | 'or';

export interface PmFilterState {
  query: string;
  combinator: PmFilterCombinator;
  rules: PmFilterRule[];
}

export function normalizePmFilterState(input: any): PmFilterState {
  const query = typeof input?.query === 'string' ? input.query : '';
  const combinator: PmFilterCombinator = input?.combinator === 'or' ? 'or' : 'and';
  const rules: PmFilterRule[] = Array.isArray(input?.rules)
    ? input.rules
        .filter((r: any) => r && typeof r === 'object')
        .map((r: any) => ({
          field: typeof r.field === 'string' ? r.field : '',
          operator: (typeof r.operator === 'string' && ALLOWED_OPERATORS.has(r.operator) ? r.operator : 'contains') as PmFilterOperator,
          value: r.value,
        }))
    : [];

  return { query, combinator, rules };
}

function getRecordValue(record: any, field: string): any {
  if (!record || !field) return undefined;
  if (!field.includes('.')) return record[field];
  return field.split('.').reduce((acc: any, k: string) => (acc ? acc[k] : undefined), record);
}

function isBlankString(v: any): boolean {
  return typeof v === 'string' && v.trim() === '';
}

function isEmptyRuleValue(value: any, operator: PmFilterOperator): boolean {
  if (operator === 'between') {
    if (!Array.isArray(value)) return true;
    const a = value[0];
    const b = value[1];
    return a === undefined || a === null || isBlankString(a) || b === undefined || b === null || isBlankString(b);
  }

  if (operator === 'in' || operator === 'not_in') {
    if (!Array.isArray(value)) return true;
    return value.length === 0;
  }

  if (value === undefined || value === null) return true;
  if (isBlankString(value)) return true;
  return false;
}

function asLowerString(v: any): string {
  if (v === undefined || v === null) return '';
  return String(v).toLowerCase();
}

function tryParseNumber(v: any): number | null {
  if (v === undefined || v === null) return null;
  const n = typeof v === 'number' ? v : Number(String(v));
  return Number.isFinite(n) ? n : null;
}

function tryParseDateMs(v: any): number | null {
  if (v === undefined || v === null) return null;
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isFinite(t) ? t : null;
  }
  const t = Date.parse(String(v));
  return Number.isFinite(t) ? t : null;
}

function compareValues(a: any, b: any): number | null {
  const an = tryParseNumber(a);
  const bn = tryParseNumber(b);
  if (an !== null && bn !== null) return an - bn;

  const ad = tryParseDateMs(a);
  const bd = tryParseDateMs(b);
  if (ad !== null && bd !== null) return ad - bd;

  const as = asLowerString(a);
  const bs = asLowerString(b);
  if (as === '' && bs === '') return 0;
  return as.localeCompare(bs);
}

export function matchesPmRule(recordValue: any, rule: PmFilterRule): boolean {
  const op = rule.operator;
  const value = rule.value;

  if (op === 'between') {
    const min = Array.isArray(value) ? value[0] : undefined;
    const max = Array.isArray(value) ? value[1] : undefined;
    const cMin = compareValues(recordValue, min);
    const cMax = compareValues(recordValue, max);
    if (cMin === null || cMax === null) return false;
    return cMin >= 0 && cMax <= 0;
  }

  if (op === 'greater_than') {
    const c = compareValues(recordValue, value);
    if (c === null) return false;
    return c > 0;
  }

  if (op === 'less_than') {
    const c = compareValues(recordValue, value);
    if (c === null) return false;
    return c < 0;
  }

  if (op === 'in' || op === 'not_in') {
    const list = Array.isArray(value) ? value.map((v: any) => asLowerString(v)) : [];
    const recordList = Array.isArray(recordValue)
      ? recordValue.map((v: any) => asLowerString(v))
      : [asLowerString(recordValue)];
    const hit = recordList.some(v => list.includes(v));
    return op === 'in' ? hit : !hit;
  }

  if (op === 'equals') {
    if (Array.isArray(recordValue)) {
      const rv = recordValue.map((v: any) => asLowerString(v));
      return rv.includes(asLowerString(value));
    }
    return asLowerString(recordValue) === asLowerString(value);
  }

  if (op === 'contains') {
    const needle = asLowerString(value);
    if (Array.isArray(recordValue)) {
      return recordValue.some((v: any) => asLowerString(v).includes(needle));
    }
    return asLowerString(recordValue).includes(needle);
  }

  if (op === 'starts_with') {
    const needle = asLowerString(value);
    if (Array.isArray(recordValue)) {
      return recordValue.some((v: any) => asLowerString(v).startsWith(needle));
    }
    return asLowerString(recordValue).startsWith(needle);
  }

  if (op === 'ends_with') {
    const needle = asLowerString(value);
    if (Array.isArray(recordValue)) {
      return recordValue.some((v: any) => asLowerString(v).endsWith(needle));
    }
    return asLowerString(recordValue).endsWith(needle);
  }

  return false;
}

export function matchesPmFilters(record: any, stateInput: any, queryFields: string[] = []): boolean {
  const state = normalizePmFilterState(stateInput);

  const query = state.query.trim().toLowerCase();
  if (query) {
    const fields = queryFields.length ? queryFields : ['name', 'title', 'description'];
    const hit = fields.some(f => asLowerString(getRecordValue(record, f)).includes(query));
    if (!hit) return false;
  }

  const validRules = state.rules.filter(r => r.field && !isEmptyRuleValue(r.value, r.operator));
  if (validRules.length === 0) return true;

  const results = validRules.map(r => matchesPmRule(getRecordValue(record, r.field), r));
  return state.combinator === 'or' ? results.some(Boolean) : results.every(Boolean);
}

export function applyPmFilters<T>(items: T[], stateInput: any, queryFields: string[] = []): T[] {
  const state = normalizePmFilterState(stateInput);
  const query = state.query.trim();
  const validRules = state.rules.filter(r => r.field && !isEmptyRuleValue(r.value, r.operator));
  if (!query && validRules.length === 0) return items;
  return (items || []).filter(item => matchesPmFilters(item as any, state, queryFields));
}
